import { OhApi } from "@shared/libs/siayuda";
import {
  CalendarEvent,
  CollaboratorAgency,
  CollaboratorChatSettings,
  CollaboratorLeave,
  CollaboratorNotification,
  CollaboratorTask,
  CollaboratorTasksFilters,
  SearchContractorCollaboratorsAgencies,
  UpdateCollaboratorChatSettings,
  Vocal,
} from "hassibot/services_v2/collaborator/types";
import { DateTime } from "luxon";

import { client } from "..";
import {
  ALL_ENTITY_STORES_NAMES,
  indexedDBCache,
  makeClassicTimeBasedCacheEvictionStrategy,
} from "../common/cache";
import {
  AgencyUuid,
  Collaborator,
  CollaboratorDsitUuid,
  CollaboratorTaskUuid,
  DSITJob,
  EventUuid,
  EventVocalUuid,
  Filters,
  JsonValue,
  Pager,
  Sort,
} from "../common/types";
import { syncCoordinatedUpdateCanary } from "../service-worker/coordinated-update-canary";
import { MainCacheKeys } from "../static";

import {
  calendarEventsReader,
  collaboratorAgenciesReader,
  collaboratorChatSettingsReader,
  collaboratorLeavesReader,
  collaboratorNotificationsReader,
  collaboratorReader,
  collaboratorsReader,
  collaboratorTaskReader,
  collaboratorTasksReader,
  searchContractorCollaboratorsAgenciesReader,
  vocalReader,
  vocalsReader,
} from "./reader";
import { asyncRoutes, routes } from "./routes";
import { CollaboratorTaskUpdateBodyInput, VocalUpdateBodyInput } from "./types";
import { collaboratorTaskPutWriter, vocalUpdateWriter } from "./writer";

const _collaborators = (): Promise<OhApi.ApiResponse<Collaborator[]>> => {
  return indexedDBCache.getOrCreate<MainCacheKeys, Collaborator[]>(
    ALL_ENTITY_STORES_NAMES.StaticEntries,
    "collaborators",
    () =>
      client
        .get(routes.collaborators({}))
        .then(OhApi.applyReader((resp: JsonValue) => resp.collaborators)),
    makeClassicTimeBasedCacheEvictionStrategy()
  );
};

export const getAllCollaboratorVocals = (): Promise<OhApi.ApiResponse<Vocal[]>> => {
  return client.get(routes.listVocals()).then(OhApi.applyReader(vocalsReader));
};

export const getOneCollaboratorTask = (
  taskUuid: CollaboratorTaskUuid
): Promise<OhApi.ApiResponse<CollaboratorTask>> => {
  return client
    .get(routes.collaboratorsTask(taskUuid))
    .then(OhApi.applyReader(collaboratorTaskReader));
};

export const putCollaboratorTask = (
  taskUuid: CollaboratorTaskUuid,
  data: CollaboratorTaskUpdateBodyInput
): Promise<OhApi.ApiResponse<CollaboratorTask>> => {
  return client
    .putJson(routes.collaboratorsTask(taskUuid), collaboratorTaskPutWriter(data))
    .then(OhApi.applyReader(collaboratorTaskReader));
};

export const getTasks = (params?: {
  filters?: CollaboratorTasksFilters;
  pager?: Pager;
  sort?: Sort;
}): Promise<OhApi.ApiResponse<CollaboratorTask[]>> => {
  return client
    .get(routes.collaboratorsTasks(params || {}))
    .then(OhApi.applyReader(collaboratorTasksReader));
};

export const getAllCollaborators = (params?: {
  filters?: Filters;
}): Promise<OhApi.ApiResponse<Collaborator[]>> => {
  // Send a real request in case of a special request with filters coming from the front end
  if (params && "filters" in params) {
    return client
      .get(routes.collaborators({ filters: params.filters }))
      .then(OhApi.applyReader((resp: JsonValue) => resp.collaborators));
  }
  // Otherwise we can fetch it directly from the local IDB representation
  else {
    return (
      _collaborators()
        // this is the tricky part, apply reader outside of cache otherwise indexedDB will strip functions
        .then(OhApi.applyReader(collaboratorsReader))
    );
  }
};

export const getCollaboratorsAgencies = (
  agenciesUuids: AgencyUuid[],
  jobs?: DSITJob[],
  maxPostingRank?: number
): Promise<OhApi.ApiResponse<CollaboratorAgency[]>> => {
  return client
    .get(routes.collaboratorsAgencies(agenciesUuids, jobs, maxPostingRank))
    .then(OhApi.applyReader(collaboratorAgenciesReader));
};

export const getContractorCollaboratorsAgencies = (
  postalCode?: string,
  inseeCode?: string,
  dsitJobs?: DSITJob[]
): Promise<OhApi.ApiResponse<SearchContractorCollaboratorsAgencies>> => {
  return client
    .get(routes.contractorCollaboratorsAgencies(postalCode, inseeCode, dsitJobs))
    .then(OhApi.applyReader(searchContractorCollaboratorsAgenciesReader));
};

export const getOneCollaborator = (
  email: string
): Promise<OhApi.ApiResponse<Collaborator | null>> =>
  indexedDBCache
    .getOrCreate(
      ALL_ENTITY_STORES_NAMES.StaticEntries,
      `collaborator-${email}` as MainCacheKeys,
      () => _collaborators().then(OhApi.applyReader(collabs => collabs.find(c => c.mail === email)))
    )
    // this is the tricky part, apply reader outside of cache otherwise indexedDB will strip functions
    .then(OhApi.applyReader(collaboratorReader));

export const getOneCollaboratorByDsitUuid = (
  dsitUuid: string
): Promise<OhApi.ApiResponse<Collaborator>> =>
  indexedDBCache
    .getOrCreate(
      ALL_ENTITY_STORES_NAMES.StaticEntries,
      `collaborator-${dsitUuid}` as MainCacheKeys,
      () =>
        _collaborators().then(
          OhApi.applyReader(collabs => collabs.find(c => c.dsitUuid === dsitUuid))
        )
    )
    // this is the tricky part, apply reader outside of cache otherwise indexedDB will strip functions
    .then(OhApi.applyReader(collaboratorReader));

export const getMe = (): Promise<OhApi.ApiResponse<Collaborator>> =>
  client
    .get(routes.me())
    .then(OhApi.applyReader(collaboratorReader))
    .then(response => {
      if (OhApi.isSuccess(response)) {
        syncCoordinatedUpdateCanary(response.value.featureFlags);
      }
      return response;
    });

export const getCollaboratorChatSettings = (
  collaboratorDsitUuid: CollaboratorDsitUuid
): Promise<OhApi.ApiResponse<CollaboratorChatSettings>> => {
  return client
    .get(routes.chatSettings(collaboratorDsitUuid))
    .then(OhApi.applyReader(collaboratorChatSettingsReader));
};

export const updateCollaboratorChatSettings = (
  collaboratorDsitUuid: CollaboratorDsitUuid,
  newSettings: UpdateCollaboratorChatSettings
): Promise<OhApi.ApiResponse<CollaboratorChatSettings>> => {
  return client
    .putJson(routes.chatSettings(collaboratorDsitUuid), newSettings)
    .then(OhApi.applyReader(collaboratorChatSettingsReader));
};

export const getCollaboratorNotifications = (
  createdAtAfter: DateTime
): Promise<OhApi.ApiResponse<CollaboratorNotification[]>> => {
  const url = routes.collaboratorNotifications(createdAtAfter);

  return client.get(url).then(OhApi.applyReader(collaboratorNotificationsReader));
};

export const postCollaboratorNotificationsReadAll = (): Promise<OhApi.ApiResponse<void>> => {
  return client.post(routes.collaboratorNotificationsReadAll(), undefined);
};

export const postCollaboratorNotificationRead = (
  notificationUuid: string
): Promise<OhApi.ApiResponse<void>> => {
  return client.post(routes.collaboratorNotificationRead(notificationUuid), undefined);
};

export const postVocal = (eventUuid: EventUuid, file: Blob): Promise<OhApi.ApiResponse<Vocal>> => {
  const formData = new FormData();

  formData.append("audioFile", file, "recording.webm");
  formData.append("eventUuid", eventUuid);

  return client.post(asyncRoutes.sendVocal(), formData).then(OhApi.applyReader(vocalReader));
};

export const deleteVocal = (vocalUuid: EventVocalUuid): Promise<OhApi.ApiResponse<void>> => {
  return client.httpDelete(routes.deleteVocal(vocalUuid));
};

export const updateVocal = (
  vocalUuid: EventVocalUuid,
  data: VocalUpdateBodyInput
): Promise<OhApi.ApiResponse<Vocal>> => {
  return client
    .putJson(routes.updateVocal(vocalUuid), vocalUpdateWriter(data))
    .then(OhApi.applyReader(vocalReader));
};

export const getCollaboratorsLeaves = (
  dsitUuids: CollaboratorDsitUuid[],
  overlapStartsAt?: DateTime,
  overlapEndsAt?: DateTime
): Promise<OhApi.ApiResponse<CollaboratorLeave[]>> => {
  return client
    .get(routes.collaboratorsLeaves(dsitUuids, overlapStartsAt, overlapEndsAt))
    .then(OhApi.applyReader(collaboratorLeavesReader));
};

export const getCollaboratorCalendarEvents = (
  collaboratorDsitUuid: CollaboratorDsitUuid,
  timeMin: DateTime,
  timeMax: DateTime
): Promise<OhApi.ApiResponse<CalendarEvent[]>> => {
  return client
    .get(asyncRoutes.calendarEvents(collaboratorDsitUuid, timeMin, timeMax))
    .then(OhApi.applyReader(calendarEventsReader));
};
